import React, { useCallback, useState, useEffect } from 'react'
import { MerchandiseForm } from '@EUForms/invoice'
import { useTranslation } from '@hooks/useTranslation'
import { Message } from 'semantic-ui-react'
import AdditionalDocDetail from './AdditionalDocument'
import PreviousDocDetail from './PreviousDocument'
import SupportingDocDetail from './SupportingDocument'
import ToggleButton from '@commonComponents/formComponents/toggleButton'
import {CustomLoader} from '@commonComponents/formComponents/loader'
import ErrorBoundary from '@commonComponents/ErrorBoundary'
import MerchContainer from './MerchContainer'
import MerchandiseSummary from '@EUForms/invoice/MerchandiseSummary'
import MerchandiseDetail from './MerchandiseDetail'
import { itemActions } from '../../reducer/ItemSlice'
import RepeatableItemsContainer from '@containers/repeatableItemsContainer'
import { useMultipleMerchandiseItem } from '@EUForms/hooks/useMultipleMerchandiseItem'
import { useEvent } from '@hooks/useEvent'
import { useDispatch, useSelector } from 'react-redux'
import { selectPreviousProcedureList, selectSpecialCountryList, selectexemptedCodeList } from '@EUForms/selectors'
import { addExtraSpace } from '@EUForms/utils'
import PackagingDocument from '@EUForms/invoice/PackagingDocument'
import MerchandiseParties from './merchandiseParties'
import CopyRepElement from '@commonComponents/copyRepElement'
import { useCurrentShipmentType } from '@hooks/useCurrentShipmentType'
import { fieldPatterns } from '../../util/fieldPatterns'
import {
  createRepElemMsgStructure,
  createLineItemMsgStructure,
} from '../../util/itemsTabValidation'
import { updateValidationErrors } from '../../reducer/validationSlice'
import validate from 'validate.js'
import { useContext } from 'react'
import { InvoiceToggleContext } from '@commonComponents/accordion/AccordianInvoice'
import useNotEnable from '../../hooks/useNotEnable'
import MerchandiseSummaryEditable from './MerchandiseSummaryEditable'

const MerchandiseContainer = ({ invoiceId, merchandiseId, invoiceIndex, itemIndex }) => {
  const {state}=useContext(InvoiceToggleContext)
  const active=state[merchandiseId]
  const translate = useTranslation()
  const { isLoading, saveForm, deleteForm } = useEvent('invoiceTab', false)
  const dispatch = useDispatch()
  const { isDisabled, isImportShipment } = useCurrentShipmentType()
  const countryList = useSelector(selectSpecialCountryList)
  const proceduresList = useSelector(selectPreviousProcedureList)
  const exemptedCodeList = useSelector(selectexemptedCodeList)
  const fieldPattern = fieldPatterns(isImportShipment,translate)
  const disabled = useNotEnable()


  const {
    updateMerchandiseItem,
    deleteMerchandiseItem,
    updateInvoiceUpperHeader,
    updateInvoiceHeader,
  } = itemActions
  const { ...rest } = useMultipleMerchandiseItem({ invoiceId, merchandiseId })
  const dataSource = useSelector(
    (state) => state.invoiceReducer.invoice.merchandiseMap[merchandiseId]
  )
  const saveData = useCallback(
    async ({ data }) => {
      const response = await saveForm(
        { merchandiseMap: { [merchandiseId]: { ...addExtraSpace(data), invoiceId } } },
        false
      )
      if (response) {
        const invoiceUpperHeader = response?.invoiceTab?.invoiceUpperHeader
        invoiceUpperHeader && dispatch(updateInvoiceUpperHeader(invoiceUpperHeader))
        const invoiceMap = response?.invoiceTab?.invoiceMap
        const invoiceHeader = (invoiceMap && invoiceMap[invoiceId]?.invoiceHeader) || ''
        invoiceHeader && dispatch(updateInvoiceHeader({ invoiceId, data: invoiceHeader }))
        const merchandiseMap = response?.invoiceTab?.merchandiseMap
        const merchData = merchandiseMap && merchandiseMap[merchandiseId]
        dispatch(updateMerchandiseItem({ merchandiseId, data: merchData }))
      }
    },
    [saveForm, invoiceId, merchandiseId]
  )
  const deleteData = useCallback(async () => {
    const response = await deleteForm({ merchandiseIdList: [merchandiseId] })
    if (response) {
      const invoiceUpperHeader = response?.invoiceTab?.invoiceUpperHeader
      invoiceUpperHeader && dispatch(updateInvoiceUpperHeader(invoiceUpperHeader))
      const invoiceHeader = response?.invoiceTab?.invoiceMap[invoiceId]?.invoiceHeader
      invoiceHeader && dispatch(updateInvoiceHeader({ invoiceId, data: invoiceHeader }))
      dispatch(deleteMerchandiseItem({ merchandiseId, invoiceId }))
    }
  }, [
    invoiceId,
    merchandiseId,
    deleteMerchandiseItem,
    updateInvoiceUpperHeader,
    updateInvoiceHeader,
    deleteForm,
  ])

  const validateLineItem = (name, value) => {
    let key = `invoice${invoiceIndex + 1}_lineItem${itemIndex + 1}_${name}`
    let lineErr =
      validate({ [name]: value }, { [name]: fieldPattern()?.itemsTab?.lineItem[name] }) || {}
    lineErr = createLineItemMsgStructure(lineErr, invoiceIndex, itemIndex,translate)
    dispatch(
      updateValidationErrors({
        tabName: 'itemsTab',
        errors: { ...lineErr },
        errorKeys: [key],
      })
    )
  }
  const { addItem, deleteItem } = rest
  const {
    supportingDocumentsList = [],
    additionalDocumentsList = [],
    taricAdditionalCodesList=[],
    nationalAdditionalCodesList=[],
  } = dataSource

  const submit = useCallback(async (payload) => {
    const { additionalCode1, antiDumping, supUnitType, controls } = payload
    let additionalCode2 = antiDumping[0] ? antiDumping[0] : ''
    let doc = []
    doc.push(antiDumping[2])
    doc.push(controls[2])
    let docs = doc?.filter(Boolean)

    // removes the old docs
    const typeValuesToRemove = exemptedCodeList?.map((type) => type.value)
    const supDocList = supportingDocumentsList?.filter(
      (item) => !typeValuesToRemove?.includes(item.type)
    )
    const addDocList = additionalDocumentsList?.filter(
      (item) => !typeValuesToRemove?.includes(item.type)
    )
    supDocList?.forEach((doc) => deleteItem(doc.id, 'supportingDocumentsList'))
    addDocList?.forEach((doc) => deleteItem(doc.id, 'additionalDocumentsList'))
    taricAdditionalCodesList?.forEach((doc) => deleteItem(doc.id, 'taricAdditionalCodesList'))
    nationalAdditionalCodesList?.forEach((doc) => deleteItem(doc.id, 'nationalAdditionalCodesList'))

    if (supUnitType) {
      let data = { unitOfMeasurement: supUnitType }
      let response = await saveForm(
        { merchandiseMap: { [merchandiseId]: { ...addExtraSpace(data), invoiceId } } },
        false
      )
      response && dispatch(updateMerchandiseItem({ merchandiseId, data }))
    }
    let addCode1 = additionalCode1 ? additionalCode1 : additionalCode2
    let addCode2 = additionalCode1 ? additionalCode2 : ''
    addCode1 && addItem('taricAdditionalCodesList', { code: addCode1 })
    addCode2 && addItem('nationalAdditionalCodesList', { code: addCode2 })
    docs.forEach((elem) =>
      addItem(elem.startsWith('C') ? 'supportingDocumentsList' : 'additionalDocumentsList', {
        type: elem,
      })
    )
  }, [])
  return (
    <ErrorBoundary>
      <div className={'accordian-section tab-contentInvoice '}>
        <Message style={{ padding: '10px' }}>
          {isLoading && <CustomLoader />}
          <ToggleButton
            id={`${
              active ? 'active' : 'inActive'
            }-expandCollapseMerch-${merchandiseId}`} // `inActive-expandCollapseMerch-${error?.merchandiseId}`
            title={(isActive) =>
              isActive ? (
                translate('MERCHANDISE')
              ) : (isImportShipment ?
                <MerchandiseSummary
                  id={merchandiseId}
                  dataSource={dataSource}
                  translate={translate}
                /> :
                <MerchandiseDetail
                  invoiceId={invoiceId}
                  merchandiseId={merchandiseId}
                  saveData={saveData}
                  updateData={updateMerchandiseItem}
                  renderForm={(params) => (
                    <MerchandiseSummaryEditable
                      {...params}
                      invoiceId={invoiceId}
                      merchandiseId={merchandiseId}
                      // originCountry={originCountry}
                      // destinationCountry={destinationCountry}
                      countryList={countryList}
                      proceduresList={proceduresList}
                      submit={submit}
                    />
                  )}
                />
              )
            }
            toggleId = {merchandiseId}
            confirmationMsg='Merchandise Line Item'
            onDelete={() => deleteData({ invoiceId })}
            lineNumber={itemIndex + 1}
            wrapButtonWhenInActive={true}
            containerStyle={{ height: '50px' }}
          />
          {active && (
            <>
              <MerchandiseDetail
                saveData={saveData}
                merchandiseId={merchandiseId}
                updateData={updateMerchandiseItem}
                renderForm={(params) => (
                  <MerchandiseForm
                    isDisabled={isDisabled}
                    submit={submit}
                    packages={(packagesList) => (
                      <PackagingDocument
                        invoiceId={invoiceId}
                        merchandiseId={merchandiseId}
                        packagesList={packagesList}
                        fieldPattern={fieldPattern}
                        createRepElemMsgStructure={createRepElemMsgStructure}
                        invoiceIndex={invoiceIndex}
                        itemIndex={itemIndex}
                        disabled={disabled}
                        {...rest}
                      />
                    )}
                    countryList={countryList}
                    invoiceId={invoiceId}
                    validateLineItem={validateLineItem}
                    copyDocument={(request, handleCopyEnabled) => (
                      <CopyRepElement
                        translate={translate}
                        title='Testing Text'
                        request={request}
                        callback={rest?.copyData}
                        isFormDisabled={isDisabled}
                        tableName={'dangerousGoodsCodesList'}
                        extraCallback={handleCopyEnabled}
                      />
                    )}
                    disabled={disabled}
                    {...params}
                  />
                )}
              />
              <MerchandiseParties
                translate={translate}
                invoiceId={invoiceId}
                merchandiseId={merchandiseId}
                invoiceIndex={invoiceIndex}
                itemIndex={itemIndex}
              />
              <RepeatableItemsContainer
                {...rest}
                tableName='previousDocumentsList'
                merchandiseId={merchandiseId}
                reducerName='invoiceReducer'
                title='PREVIOUS_DOCUMENT'
                translate={translate}
                boxNumber='2/1'
                renderForm={(params) => (
                  <PreviousDocDetail
                    fieldPattern={fieldPattern}
                    createRepElemMsgStructure={createRepElemMsgStructure}
                    invoiceIndex={invoiceIndex}
                    itemIndex={itemIndex}
                    {...params}
                  />
                )}
              />
              <RepeatableItemsContainer
                {...rest}
                tableName='additionalDocumentsList'
                merchandiseId={merchandiseId}
                reducerName='invoiceReducer'
                title='ADDITIONAL_DOCUMENT'
                translate={translate}
                boxNumber='2/2'
                renderForm={(params) => (
                  <AdditionalDocDetail
                    fieldPattern={fieldPattern}
                    createRepElemMsgStructure={createRepElemMsgStructure}
                    invoiceIndex={invoiceIndex}
                    itemIndex={itemIndex}
                    {...params}
                  />
                )}
              />
              <RepeatableItemsContainer
                {...rest}
                tableName='supportingDocumentsList'
                merchandiseId={merchandiseId}
                reducerName='invoiceReducer'
                title='SUPP_DOCS'
                translate={translate}
                boxNumber='2/3'
                renderForm={(params) => (
                  <SupportingDocDetail
                    fieldPattern={fieldPattern}
                    createRepElemMsgStructure={createRepElemMsgStructure}
                    invoiceIndex={invoiceIndex}
                    itemIndex={itemIndex}
                    {...params}
                  />
                )}
              />
              <MerchContainer merchandiseId={merchandiseId} {...rest} />
            </>
          )}
        </Message>
      </div>
    </ErrorBoundary>
  )
}

export default React.memo(MerchandiseContainer)
